// src/settings/ProfileManagement.tsx
import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

type SettingsButtonProps = {
  to: string;
  activeMatch?: string;
  children: React.ReactNode;
};

const buttonBase: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  padding: '12px 16px',
  borderRadius: 10,
  border: '1px solid rgba(148,163,184,0.6)',
  background: '#111827',
  color: '#e5e7eb',
  textDecoration: 'none',
  fontWeight: 700,
  fontSize: 14,
  cursor: 'pointer',
  boxShadow: '0 1px 2px rgba(0,0,0,0.4)',
};

const activeStyles: React.CSSProperties = {
  background: "#2d6cdf",
  color: "#ffffff",
  borderColor: "#2d6cdf",
};

const SettingsButton: React.FC<SettingsButtonProps> = ({ to, activeMatch, children }) => {
  const location = useLocation();
  const match = activeMatch ?? to;

  return (
    <NavLink
      to={to}
      end={false}
      style={({ isActive }) => {
        const isMatched = isActive || location.pathname.startsWith(match);
        return isMatched ? { ...buttonBase, ...activeStyles } : buttonBase;
      }}
    >
      {children}
    </NavLink>
  );
};

const ProfileManagementMobile: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#02040b' }}>
      <main
        style={{
          padding: '45px 38px 24px 32px',
          boxSizing: 'border-box',
          color: '#e5e7eb',
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: 960,
          }}
        >
          <h2
            style={{
              marginTop: 0,
              fontSize: 24,
              fontWeight: 700,
            }}
          >
            {t('profileManagement.page.title')}
          </h2>
          <p
            style={{
              color: '#9ca3af',
              marginTop: 6,
              fontSize: 13,
            }}
          >
            {t('profileManagement.page.subtitle')}
          </p>

          <div
            style={{
              marginTop: 18,
              borderRadius: 12,
              border: '1px solid rgba(148,163,184,0.4)',
              background: 'rgba(15,23,42,0.9)',
              padding: 16,
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: 12,
              }}
            >
              <SettingsButton to="/profile" activeMatch="/profile">
                👻 {t('profileManagement.buttons.profile')}
              </SettingsButton>
              <SettingsButton to="/my-posts" activeMatch="/my-posts">
                📭 {t('profileManagement.buttons.postings')}
              </SettingsButton>
              <SettingsButton to="/notifications" activeMatch="/notifications">
                🔔 {t('profileManagement.buttons.notifications')}
              </SettingsButton>
              <SettingsButton to="/following-list" activeMatch="/following-list">
                📚 {t('profileManagement.buttons.favorites')}
              </SettingsButton>
              <SettingsButton to="/followers-list" activeMatch="/followers-list">
                📚 {t('profileManagement.buttons.followers')}
              </SettingsButton>
              <SettingsButton to="/blocked" activeMatch="/blocked">
                🚫 {t('profileManagement.buttons.blocked')}
              </SettingsButton>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ProfileManagementMobile;
